import { useCallback, useEffect, useMemo, useState } from 'react';

import { useParams } from 'react-router';

import Card from '@mui/material/Card';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AddIcon from '@mui/icons-material/Add';
import { MenuItem, SelectChangeEvent } from '@mui/material';

import PageContainer from '../../../components/Redesing/page-container';
import {
  IColumn,
  ISortConfig,
} from '../../../ts/interface/components/redesing/table.interface';
import PageContent from '../../../components/Redesing/page-content';
import { PaginationTable } from '../../../components/Redesing/table/pagination-table';
import { MovaticFieldGroupSingleSelectFilter } from '../../../components/Redesing/movatic-field-group';

import useUrlState from '../../../hooks/use-url-state';

import { SearchChip } from '../../../ts/interface/components/table.interface';
import { IStation } from '../../../ts/interface/station.interface';

import { DEFAULT_ROWS_PER_PAGE, LOCKER } from '../../../constants';
import { LocationStatus } from '../../../ts/enums';
import { changeTab, findAccess, history } from '../../../utils';

import { stationConfigurations } from '../../../util/stations';
import StationLocationsMap from '../stationLocationsMap';
import useOrganization from '../../../hooks/use-organization';
import useSearchDebounce from '../../../hooks/search/useSearchDebounce';
import { makeColumnsV2 } from '../../../tableColumns/stationColumns';
import { ISystem } from '../../../ts/interface/system.interface';
import OrganizationSystemMultiSelect from '../../../components/system/organization-system-multi-select';
import { useKeysetPagination } from '../../../hooks/use-keyset-pagination';
import {
  useLocationsRelayQuery,
  mapLocationsToIStations,
  LocationQueryFilter,
} from '../../../hooks/stations/query/V2';
import { STATION_STATUS, statusToStateNumber, sortKeyToField } from '../shared';

function OrgLocationIndex() {
  const organizationInfo = useOrganization();

  const { currentTab = 'locations' } = useParams<{ currentTab: string }>();
  const [currentTabV, setCurrentTabV] = useState(currentTab);
  const [exportData, setExportData] = useState<boolean>(false);
  const [selectFilter, setSelectFilter] = useState<string>(STATION_STATUS[0].value);

  // Shared server-side keyset pagination (page# kept local, not in the URL).
  const {
    pageNumber,
    after,
    isPaginating,
    onPageChange,
    onPageLoaded,
    reset: resetPagination,
  } = useKeysetPagination();

  const [pageState, setPageState, isComplete, isRestoring] = useUrlState({
    sizePerPage: DEFAULT_ROWS_PER_PAGE,
    status: STATION_STATUS[0].value as LocationStatus | string,
    selectedSystem: [] as { value: string; label: string; original: ISystem }[],
    system: '',
    search: null as string | null,
    sortConfig: {
      key: 'name',
      direction: 'asc',
    } as ISortConfig,
  });

  const [chips, setChips] = useState<SearchChip[]>([]);

  const debouncedSearchTerm = useSearchDebounce(pageState.search);

  const isEnabled = isComplete || Boolean(organizationInfo?.id);

  // Build GraphQL filter
  const graphqlFilter = useMemo((): LocationQueryFilter => {
    const filter: LocationQueryFilter = {
      organizationId: organizationInfo.id,
    };

    // Add state filter if not 'all'
    const stateNumber = statusToStateNumber(pageState.status);
    if (stateNumber !== undefined) {
      filter.state = stateNumber;
    }

    // Add name search filter
    if (debouncedSearchTerm) {
      filter.name = debouncedSearchTerm;
    }

    // Add system filter server-side (applied systems, comma-separated ids)
    const systemIds = pageState.system.split(',').filter((id: string) => Boolean(id));
    if (systemIds.length > 0) {
      filter.systemIds = systemIds;
    }

    return filter;
  }, [organizationInfo.id, pageState.status, debouncedSearchTerm, pageState.system]);

  // Build GraphQL sort
  const graphqlSort = useMemo(() => {
    return [
      {
        field: sortKeyToField(pageState.sortConfig.key) as any,
        order: pageState.sortConfig.direction.toUpperCase() as 'ASC' | 'DESC',
      },
    ];
  }, [pageState.sortConfig.key, pageState.sortConfig.direction]);

  const {
    data: locationsData,
    count: totalLocationCount,
    hasMore: graphqlHasMore,
    endCursor,
    isLoading: isLoadingAllStations,
    isRefetching,
  } = useLocationsRelayQuery(graphqlFilter, {
    limit: pageState.sizePerPage,
    after,
    sort: graphqlSort,
    enabled: isEnabled,
  });

  // Only the current page's rows are shown (server-side pagination).
  const stations = useMemo(() => {
    if (!locationsData) return [];
    return mapLocationsToIStations(locationsData);
  }, [locationsData]);

  // Record the next page's cursor and clear the paginating loader when a fetch
  // settles.
  useEffect(() => {
    onPageLoaded(endCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationsData]);

  // Rebuild chips from the *applied* filters (the URL-persisted fields that
  // drive the query), so chips always match the fetched data — including after
  // a refresh and when a filter was cleared (no chip).
  useEffect(() => {
    if (isRestoring) return;

    const next: SearchChip[] = [];

    // Treat "All" as no status filter (consistent with the Hardware page): no
    // chip when nothing meaningful is selected.
    if (pageState.status && pageState.status !== LocationStatus.all) {
      const option = STATION_STATUS.find((item) => item.value === pageState.status);
      next.push({
        label: 'Status',
        field: 'status',
        value: pageState.status,
        displayValue: option?.label ?? pageState.status,
      });
    }

    pageState.system
      ?.split(',')
      .filter(Boolean)
      .forEach((value: string) => {
        const system = pageState.selectedSystem?.find((s) => s.value === value);
        next.push({
          label: 'System',
          field: 'system',
          value,
          displayValue: system?.label ?? value,
        });
      });

    setChips(next);
  }, [isRestoring, pageState.status, pageState.system, pageState.selectedSystem]);

  useEffect(() => {
    if (currentTab !== currentTabV) {
      setCurrentTabV(currentTab);
    }
  }, [currentTab, currentTabV]);

  const handleTabsChange = useCallback(
    (event: React.SyntheticEvent, value: React.SetStateAction<string>) => {
      changeTab(value, currentTab, 'locations');
    },
    [currentTab]
  );

  const handleChipDelete = useCallback(
    (deletedGroup: SearchChip) => {
      const { label } = deletedGroup;
      resetPagination();

      if (label === 'Status') {
        setSelectFilter(STATION_STATUS[0].value);
        setPageState((prev) => ({
          ...prev,
          sizePerPage: DEFAULT_ROWS_PER_PAGE,
          status: '',
        }));
      }

      if (label === 'System') {
        setPageState((prev) => ({
          ...prev,
          sizePerPage: DEFAULT_ROWS_PER_PAGE,
          selectedSystem: [],
          system: '',
        }));
      }
    },
    [setPageState, resetPagination]
  );

  const handleTypeChange = useCallback(
    (selectedOption: { value: string; label: string }): void => {
      resetPagination();
      setPageState((prev) => ({
        ...prev,
        sizePerPage: DEFAULT_ROWS_PER_PAGE,
        status: selectedOption.value as LocationStatus,
      }));
    },
    [setPageState, resetPagination]
  );

  const formatStations = (stations: IStation[]) => {
    return stations.map((station) => {
      const available = station.available_hardware || 0;
      const capacity =
        station.hardware_type_name !== LOCKER ||
        station.configuration === stationConfigurations.ZONE
          ? station.capacity
          : station.totalRentableHardware || 0;
      return { ...station, available, capacity };
    });
  };

  const showColumnSystem = true;

  const stationsItems = stations || [];

  return (
    <PageContainer isLoading={!isComplete || isRestoring} spaceName={'Locations'}>
      <PageContent
        dataId="locations"
        detailedView={false}
        tabsList={[
          { label: 'Locations', value: 'locations' },
          {
            label: 'Map',
            value: 'map',
          },
        ]}
        tabsChildren={
          <>
            {currentTabV === 'locations' && (
              <Card>
                <PaginationTable
                  dataId="locations-list"
                  isLoading={!isComplete || isRestoring || isLoadingAllStations || isPaginating}
                  isLoadingCache={isRefetching}
                  buttons={[
                    {
                      label: 'Export',
                      onClick: () => setExportData(true),
                      startIcon: <FileDownloadIcon />,
                    },
                    {
                      startIcon: <AddIcon />,
                      label: 'Add',
                      onClick: () => history.push('/locations/add'),
                      disabled: !findAccess(organizationInfo.access).includes('physical'),
                    },
                  ]}
                  exportData={exportData}
                  localSearch={false}
                  csvFileName={'Stations.csv'}
                  onExport={() => setExportData(false)}
                  chips={chips}
                  handleChipDelete={handleChipDelete}
                  spaceFilters={
                    <>
                      <MovaticFieldGroupSingleSelectFilter
                        dataId={`location-status-filter`}
                        label={'Status'}
                        value={selectFilter.toString()}
                        onChange={(e: SelectChangeEvent) => {
                          const id = e.target.value as string;
                          setSelectFilter(id);
                        }}
                        options={STATION_STATUS.map((option, index) => (
                          <MenuItem
                            id={`location-status-dropdown-${index}`}
                            key={index}
                            value={option.value}
                          >
                            {option.label}
                          </MenuItem>
                        ))}
                      />
                      <OrganizationSystemMultiSelect
                        organization_id={organizationInfo.id}
                        selectedSystem={pageState.selectedSystem}
                        onChange={(systems) => {
                          setPageState((prevState) => ({
                            ...prevState,
                            selectedSystem:
                              systems?.map((system) => ({
                                label: system?.name,
                                value: system?.gid,
                                original: system,
                              })) || [],
                          }));
                        }}
                      />
                    </>
                  }
                  onApplyFilter={() => {
                    if (selectFilter !== pageState.status) {
                      const obj = STATION_STATUS.find(
                        (item) => item.value === selectFilter
                      ) || {
                        value: 'all',
                        label: 'All',
                      };
                      handleTypeChange(obj);
                    }

                    const selectedSystemIds = pageState.selectedSystem
                      .map((item) => item.value)
                      .join(',');
                    if (selectedSystemIds !== pageState.system) {
                      resetPagination();
                      setPageState((prevState) => ({
                        ...prevState,
                        sizePerPage: DEFAULT_ROWS_PER_PAGE,
                        system: selectedSystemIds,
                      }));
                    }
                  }}
                  showFilter
                  noDataSlice
                  onRowCLick={(id: string) => history.push(`/locations/${id}/general`)}
                  sortBy={pageState.sortConfig.key}
                  sortDirection={pageState.sortConfig.direction}
                  onSortChange={(sort) => {
                    resetPagination();
                    setPageState((prev) => ({
                      ...prev,
                      sortConfig: {
                        key: sort.key,
                        direction: sort.direction as 'asc' | 'desc',
                      },
                    }));
                  }}
                  padding={{ pt: 1, pr: 1 }}
                  columns={makeColumnsV2({} as ISystem, showColumnSystem) as IColumn[]}
                  items={formatStations(stationsItems || [])}
                  searchPlaceholder={`Search Locations`}
                  page={pageNumber}
                  rowsPerPage={pageState.sizePerPage}
                  hasMore={graphqlHasMore}
                  totalItems={totalLocationCount}
                  onPageChange={(page, newPage) => onPageChange(newPage)}
                  onRowsPerPageChange={(pageSizeString) => {
                    resetPagination();
                    setPageState((prev) => ({
                      ...prev,
                      sizePerPage: Number(pageSizeString.target.value),
                    }));
                  }}
                  handleSearch={(e: React.ChangeEvent<HTMLInputElement>) => {
                    resetPagination();
                    setPageState((prev) => ({
                      ...prev,
                      search: e.target.value,
                    }));
                  }}
                  searchText={pageState.search || ''}
                />
              </Card>
            )}
            {currentTabV === 'map' && (
              <StationLocationsMap organization_id={organizationInfo.id} />
            )}
          </>
        }
        currentTabV={currentTabV}
        handleTabsChange={handleTabsChange}
      />
    </PageContainer>
  );
}

export default OrgLocationIndex;
